import { json } from "@remix-run/node";
import { getOrders } from "../controllers/orders.server";
import { Link, useLoaderData } from "@remix-run/react";
import "../styles/orders/Orders.css";
import { getSession } from "../sessions";
import Footer from "../components/Footer";
import HomePageCream from "../components/site-cream/HomePageCream";
import dayjs from "dayjs";
import { resolveSiteTheme } from "../utils/siteVariant.server";
import { FRAUNCES_LINK } from "../utils/fonts";
import { getSettings } from "../controllers/settings.server";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

// Accent serif for the marketing headlines (Inter is loaded in root).
export const links = () => [FRAUNCES_LINK];

export const meta = () => [
  { title: "Pendora Digital Downloads" },
  {
    name: "description",
    content:
      "Pendora delivers digital downloads and license keys for Shopify stores: ebooks, PDFs, audio, video, courses and software, fulfilled automatically and delivered to the thank-you page and the inbox.",
  },
];

export const loader = async ({ request }) => {
  const signal = request.signal;

  // Check if client already disconnected
  if (signal?.aborted) {
    throw new Response("Request aborted", { status: 499 });
  }

  const session = await getSession(request.headers.get("Cookie"));
  const { email } = session.data;

  // No session: this visitor is not a customer following a download link, so
  // the home route is Pendora's marketing site.
  if (!email) {
    return json({
      signedIn: false,
      orders: [],
      settings: { showBranding: true },
      theme: resolveSiteTheme(request),
    });
  }

  // Check abort before database query
  if (signal?.aborted) {
    throw new Response("Request aborted", { status: 499 });
  }

  const orders = await getOrders(email);

  // Check abort after database query
  if (signal?.aborted) {
    throw new Response("Request aborted", { status: 499 });
  }

  let settings = { showBranding: true };
  if (orders.length > 0 && orders[0].shop) {
    settings = await getSettings(orders[0].shop);
  }

  return json({ signedIn: true, orders, settings, theme: null });
};

export default function Index() {
  const { signedIn, orders, settings, theme } = useLoaderData();

  if (!signedIn) {
    return <HomePageCream theme={theme === "cream" ? "cream" : "night"} />;
  }

  // A customer with a session but no orders (deleted, revoked, or an email
  // with nothing yet) stays on the customer-facing page, in the shop's
  // branding, and never sees merchant marketing.
  if (!orders || orders.length === 0) {
    return (
      <section className="orders__section">
        <div className="orders__empty">
          <h1 className="title">Orders</h1>
          <p>
            No orders are available for this email right now. Open the
            download link from your order confirmation email, or contact the
            store if you think this is a mistake.
          </p>
        </div>
        <Footer showBranding={settings.showBranding} />
      </section>
    );
  }

  const formatOrderDate = (order) => {
    if (!order?.orderTime) {
      return "N/A";
    }

    return dayjs.utc(order.orderTime).format("DD MMM YYYY, HH:mm");
  };

  return (
    <section className="orders__section">
      <h1 className="title">Orders</h1>
      <h3 className="email">
        <span>Email: </span> {orders?.[0]?.email ?? ""}
      </h3>

      <div className="orders__container">
        {orders.map((order) => (
          <div key={order._id} className="orders__card">
            <div className="content">
              <div className="image">
                {order?.products?.[0]?.productImage && (
                  <img
                    src={order.products[0].productImage}
                    alt=""
                    loading="lazy"
                  />
                )}
              </div>

              <div className="text">
                <p>#{order?.orderId ?? ""}</p>
                <h4>
                  {order?.products?.[0]?.name
                    ? [order.products[0].name, order.products[0].variant]
                        .filter(Boolean)
                        .join(" ")
                    : "Product"}
                </h4>
                <span>Order placed</span>
                <h5>{formatOrderDate(order)}</h5>
              </div>
            </div>

            <Link to={`/orders/${order._id}`}>
              <button className="shopify__btn">View Order</button>
            </Link>
          </div>
        ))}
      </div>

      <Footer showBranding={settings.showBranding} />
    </section>
  );
}
